import React, {useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import ReactPaginate from 'react-paginate';
import {useDispatch, useSelector} from 'react-redux';
import Box from '@material-ui/core/Box';
import FormControl from '@material-ui/core/FormControl';
import Select from '@material-ui/core/Select';
import MenuItem from '@material-ui/core/MenuItem';
import {
  TablePagination,
  TablePaginationText
} from 'routes/App/dashboard/styles';
import Table from 'components/Table';
import IconRowArrow from 'assets/icon-em-row-arrow.svg';
import IconEmpty from 'assets/icon-em-empty.svg';
import SearchField from 'components/fields/SearchField';
import Loading from 'components/shared/Utils/Loading';
import {
  GET_EMAIL_MARKETING_CAMPAIGNS,
  RESET_EMAIL_MARKETING_CAMPAIGNS,
  SET_EMAIL_MARKETING_CAMPAIGNS_PAGE,
  SET_EMAIL_MARKETING_CAMPAIGNS_FILTERS
} from 'store/emailMarketing/action';
import {
  CAMPAIGNS_PAGE_SIZE,
  CAMPAIGN_STATUS_OPTIONS,
  campaignTitle,
  campaignTypeLabel,
  campaignStatusLabel,
  formatCount,
  formatRate,
  formatCampaignDate
} from 'helpers/constants/emailMarketing';
import {
  CampaignsCard,
  CampaignsHeader,
  CampaignsTitle,
  CampaignsControls,
  CampaignSubject,
  CampaignLastSent,
  CampaignStatus,
  CampaignRowButton,
  CampaignsEmpty,
  CampaignsTableWrapper,
  EmailMarketingSpinner
} from 'routes/App/emailMarketingV2/components/styles';

const getTableModel = (onOpen) => [
  {
    title: 'Subject Line',
    width: '34%',
    renderer: (item) => (
      <>
        <CampaignSubject>{campaignTitle(item)}</CampaignSubject>
        {item?.lastSentAt && (
          <CampaignLastSent>
            Last sent {formatCampaignDate(item.lastSentAt)}
          </CampaignLastSent>
        )}
      </>
    )
  },
  {
    title: 'Status',
    width: '11%',
    renderer: (item) => (
      <CampaignStatus className={item?.statusLabel}>
        {campaignStatusLabel(item)}
      </CampaignStatus>
    )
  },
  {
    title: 'Type',
    width: '13%',
    renderer: (item) => campaignTypeLabel(item)
  },
  {
    title: 'Delivered',
    width: '10%',
    renderer: (item) => formatCount(item?.delivered)
  },
  {
    title: 'Open Rate',
    width: '11%',
    renderer: (item) => formatRate(item?.openRate)
  },
  {
    title: 'Click Rate',
    width: '11%',
    renderer: (item) => formatRate(item?.clickRate)
  },
  {
    title: 'Orders',
    width: '5%',
    renderer: (item) => formatCount(item?.orders)
  },
  {
    width: '5%',
    renderer: (item) => (
      <CampaignRowButton
        type='button'
        aria-label={`Open ${campaignTitle(item)}`}
        onClick={(e) => {
          e.stopPropagation();
          onOpen(item);
        }}
      >
        <img src={IconRowArrow} alt='' />
      </CampaignRowButton>
    )
  }
];

const CampaignsTable = ({onOpenCampaign}) => {
  const dispatch = useDispatch();
  const {
    campaigns,
    campaignsPaging,
    campaignsFilters,
    campaignsSpinner,
    campaignsError
  } = useSelector((state) => state.emailMarketingReducer);
  const {locationId} = useSelector((state) => state.user);
  const [searchField, setSearchField] = useState(campaignsFilters.search);

  const {page, pageSize, total} = campaignsPaging;
  const pages = pageSize ? Math.ceil(total / pageSize) : 0;

  // Customer.io reuses ids across its campaign and newsletter namespaces, so the
  // campaign id alone is not unique within a page.
  const rows = campaigns.map((campaign) => ({
    ...campaign,
    rowKey: `${campaign.sourceType}:${campaign.campaignId}`
  }));

  const lastLocationId = useRef(locationId);

  useEffect(() => {
    if (!locationId) return;

    if (lastLocationId.current !== locationId) {
      lastLocationId.current = locationId;
      setSearchField('');
      dispatch(RESET_EMAIL_MARKETING_CAMPAIGNS());
      dispatch(
        GET_EMAIL_MARKETING_CAMPAIGNS({
          page: 1,
          pageSize: CAMPAIGNS_PAGE_SIZE,
          status: null,
          search: ''
        })
      );
      return;
    }

    dispatch(
      GET_EMAIL_MARKETING_CAMPAIGNS({
        page,
        pageSize,
        status: campaignsFilters.status,
        search: campaignsFilters.search
      })
    );
  }, [locationId, page, campaignsFilters.status, campaignsFilters.search]);

  const onPageChange = ({selected}) => {
    dispatch(SET_EMAIL_MARKETING_CAMPAIGNS_PAGE(selected + 1));
  };

  const onStatusChange = (event) => {
    dispatch(
      SET_EMAIL_MARKETING_CAMPAIGNS_FILTERS({status: event.target.value})
    );
  };

  // Guarded because this also fires on blur, and refiltering on an unchanged
  // value would bounce the merchant back to page one for nothing.
  const onSearch = () => {
    if (searchField !== campaignsFilters.search) {
      dispatch(SET_EMAIL_MARKETING_CAMPAIGNS_FILTERS({search: searchField}));
    }
  };

  const onClearSearch = () => {
    setSearchField('');
    dispatch(SET_EMAIL_MARKETING_CAMPAIGNS_FILTERS({search: ''}));
  };

  return (
    <CampaignsCard>
      <CampaignsHeader>
        <CampaignsTitle>All Campaigns</CampaignsTitle>
        <CampaignsControls>
          <span>Status</span>
          <Box className='Say2EatDrop'>
            <FormControl fullWidth variant='outlined'>
              <Select
                value={campaignsFilters.status}
                inputProps={{'aria-label': 'Campaign status'}}
                onChange={onStatusChange}
                MenuProps={{
                  anchorOrigin: {vertical: 'bottom', horizontal: 'left'},
                  transformOrigin: {vertical: 'top', horizontal: 'left'},
                  getContentAnchorEl: null
                }}
              >
                {CAMPAIGN_STATUS_OPTIONS.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Box>
          <SearchField
            placeholder='Search'
            value={searchField}
            isClearIconVisible={Boolean(searchField)}
            onChange={(e) => setSearchField(e.target.value)}
            onClear={onClearSearch}
            onKeyPress={(e) => e.key === 'Enter' && onSearch()}
            onBlur={onSearch}
          />
        </CampaignsControls>
      </CampaignsHeader>

      {campaignsSpinner && (
        <EmailMarketingSpinner>
          <Loading />
        </EmailMarketingSpinner>
      )}

      {!campaignsSpinner && !total && (
        <CampaignsEmpty>
          <img src={IconEmpty} alt='' />
          <span>
            {campaignsError
              ? 'We could not load your campaigns. Please try again.'
              : 'There are no campaigns to show yet.'}
          </span>
        </CampaignsEmpty>
      )}

      {!campaignsSpinner && Boolean(total) && (
        <>
          <CampaignsTableWrapper>
            <Table
              tableModel={getTableModel(onOpenCampaign)}
              data={rows}
              rowKey='rowKey'
              onRowClick={onOpenCampaign}
            />
          </CampaignsTableWrapper>
          {pages > 1 && (
            <TablePagination className='EmailMarketingPagination'>
              <TablePaginationText>
                Showing {campaigns.length} out of {total} campaigns
              </TablePaginationText>
              <ReactPaginate
                previousLabel='previous'
                nextLabel='next'
                breakLabel='...'
                breakClassName='break-me'
                pageCount={pages}
                initialPage={page - 1}
                forcePage={page - 1}
                marginPagesDisplayed={2}
                pageRangeDisplayed={5}
                onPageChange={onPageChange}
                containerClassName='pagination'
                subContainerClassName='pages pagination'
                activeClassName='active'
              />
            </TablePagination>
          )}
        </>
      )}
    </CampaignsCard>
  );
};

CampaignsTable.propTypes = {
  onOpenCampaign: PropTypes.func.isRequired
};

export default CampaignsTable;
